import { FC, useCallback, useMemo } from 'react';
import { Column } from 'primereact/column';
import dayjs from 'dayjs';
import { formatNumber } from '@agro1desenvolvimento/utils-js';
import {
  Atividade,
  RecomendacaoSemeaduraResumoTalhao,
  TalhaoRow,
} from '../../../../@types';
import {
  AtividadesService,
  RecomendacaoSemeaduraService,
  UnidadesService,
} from '../../../../services';
import {
  defaultNumberFormatter,
  getPeriodo,
  getRegulagem,
  getTotalLabel,
} from './util';
import { NUMBER_FORMAT_OPTIONS } from '../utils';
import AutoSortTable from '../../../../components/AutoSortTable';
import defaultColumnProps from '../../../../utils/default-column-props';
import humanizeDate from '../../../../utils/humanize-date';

const SementesList: FC<PropTypes> = ({
  formValues,
  striped = true,
  printable = false,
}) => {
  const {
    talhoes,
    sementeCobertura,
    comMargemSeguranca,
    distribuicaoRegulagemSemente,
    porcentagemCorrecaoDensidade,
    porcentagemAreaRemonte,
    porcentagemCorrecaoQuantidade,
    espacamento,
    rendimentoOperacionalMaximo,
    rendimentoOperacionalMinimo,
    atividade,
  } = formValues;

  const isCulturaPorUnidade = useMemo(
    () => AtividadesService.isMilho(atividade?.descricao),
    [atividade],
  );

  const talhoesList = useMemo(
    () =>
      talhoes.map((talhao) => {
        const params = {
          porcentagemCorrecaoDensidade: porcentagemCorrecaoDensidade || 0,
          porcentagemAreaRemonte: porcentagemAreaRemonte || 0,
          porcentagemCorrecaoQuantidade: porcentagemCorrecaoQuantidade || 0,
        };
        const fatorCorrecao =
          RecomendacaoSemeaduraService.getFatorCorrecao(params);

        const densidadeCorrigida =
          RecomendacaoSemeaduraService.getDensidadeCorrigida({
            densidade: talhao.densidade || 0,
            pg: talhao.pg || 0,
            fatorCorrecao,
          });

        const regulagem = RecomendacaoSemeaduraService.getRegulagem({
          dose: densidadeCorrigida,
          espacamento: espacamento || 0,
          distribuicaoRegulagem: distribuicaoRegulagemSemente || 0,
        });

        let quantidade;

        if (sementeCobertura) {
          const dose = talhao.dose || 0;
          quantidade = {
            minima: dose,
            maxima: dose,
            resultado: dose,
          };
        } else {
          quantidade = RecomendacaoSemeaduraService.getQuantidade({
            densidadeCorrigida,
            pms: talhao.pms || 0,
            isCulturaPorUnidade,
          });
        }

        const quantidadeTotal = RecomendacaoSemeaduraService.getQuantidadeTotal(
          {
            quantidade,
            area: talhao.area || 0,
            comMargemSeguranca,
            taxaCorrecao: porcentagemCorrecaoQuantidade || 0,
          },
        );

        const periodo = getPeriodo(
          talhao.dataInicio,
          talhao.dataTermino,
          talhao.area || 0,
          rendimentoOperacionalMaximo || 0,
          rendimentoOperacionalMinimo || 0,
        );

        return {
          ...talhao,
          densidadeCorrigida,
          regulagem: getRegulagem(talhao, regulagem, comMargemSeguranca),
          quantidade,
          quantidadeTotal,
          comMargemSeguranca,
          rendimentoOperacionalMaximo: rendimentoOperacionalMaximo || 0,
          rendimentoOperacionalMinimo: rendimentoOperacionalMinimo || 0,
          distribuicaoRegulagemSemente: distribuicaoRegulagemSemente || 10,
          dataColheita: dayjs(talhao.dataInicio)
            .add(talhao.ciclo || 0, 'day')
            .toDate(),
          dose: talhao.dose,
          periodo,
        };
      }),
    [
      talhoes,
      distribuicaoRegulagemSemente,
      comMargemSeguranca,
      porcentagemCorrecaoDensidade,
      porcentagemAreaRemonte,
      porcentagemCorrecaoQuantidade,
      espacamento,
      rendimentoOperacionalMaximo,
      rendimentoOperacionalMinimo,
      sementeCobertura,
      isCulturaPorUnidade,
    ],
  );

  const quantidadeTemplate = useCallback(
    (rowData: RecomendacaoSemeaduraResumoTalhao) => {
      const { unidade } = rowData;
      const { minima, maxima, resultado } = rowData.quantidade;
      const unidadeTaxa = unidade ? UnidadesService.getTaxaFinal(unidade) : 1;
      const unidadeAbreviatura = `${
        rowData.unidade?.abreviatura.trim() || 'kg'
      }/ha`;

      if (comMargemSeguranca) {
        const quantidadeMinima = defaultNumberFormatter(minima / unidadeTaxa);
        const quantidadeMaxima = defaultNumberFormatter(maxima / unidadeTaxa);

        return `${quantidadeMinima} a ${quantidadeMaxima} ${unidadeAbreviatura}`;
      }

      const quantidadeResultado = defaultNumberFormatter(
        resultado / unidadeTaxa,
      );

      return `${quantidadeResultado} ${unidadeAbreviatura}`;
    },
    [comMargemSeguranca],
  );

  const regulagemTemplate = useCallback(
    (rowData: RecomendacaoSemeaduraResumoTalhao) => {
      const { calculada, intervalo, resultado, unidade } = rowData.regulagem;

      return (
        <>
          {calculada}
          <br />
          {comMargemSeguranca ? intervalo : resultado} {unidade}
        </>
      );
    },
    [comMargemSeguranca],
  );

  return (
    <div>
      <h2 className="pl-2">Sementes</h2>
      <AutoSortTable
        sortField="dataInicio"
        dataKey="identification"
        value={talhoesList}
        className={`p-0 ${printable ? 'printable-table' : ''}`}
        emptyMessage="Nenhuma semente encontrada."
        sortIcon={printable ? () => null : undefined}
        stripedRows={striped}
      >
        <Column
          {...defaultColumnProps('left')}
          header="Talhão"
          field="talhao.descricao"
        />
        <Column
          {...defaultColumnProps()}
          header="Área (ha)"
          field="area"
          body={(talhao) => formatNumber(talhao.area, NUMBER_FORMAT_OPTIONS)}
        />
        <Column
          {...defaultColumnProps('left')}
          header="Cultivar"
          field="itemSaldo.descricao"
        />
        <Column
          {...defaultColumnProps()}
          header="Data Início"
          body={(talhao) => humanizeDate(talhao.dataInicio)}
          field="dataInicio"
          headerClassName="text-no-highlight"
        />
        <Column
          {...defaultColumnProps()}
          header="Data Fim"
          field="dataTerminoMinima"
          body={(talhao) =>
            `${humanizeDate(talhao.dataTerminoMinima)} a ${humanizeDate(
              talhao.dataTermino,
            )}`
          }
        />
        <Column
          {...defaultColumnProps()}
          header="Tempo (dias)"
          field="periodo"
        />
        <Column
          {...defaultColumnProps()}
          header="Dens. Ideal"
          body={(talhao) =>
            formatNumber(talhao.densidade || 0, NUMBER_FORMAT_OPTIONS)
          }
          field="densidade"
          hidden={sementeCobertura}
        />
        <Column
          {...defaultColumnProps()}
          header="PG (%)"
          body={(talhao) => formatNumber(talhao.pg || 0, NUMBER_FORMAT_OPTIONS)}
          field="pg"
          hidden={sementeCobertura}
        />
        <Column
          {...defaultColumnProps()}
          header="Dens. Corrigida"
          body={(talhao) => defaultNumberFormatter(talhao.densidadeCorrigida)}
          field="densidadeCorrigida"
          hidden={sementeCobertura}
        />
        <Column
          {...defaultColumnProps()}
          headerClassName="text-danger"
          bodyClassName="text-danger"
          className="regulagem col"
          header={`Regulagem (${distribuicaoRegulagemSemente}m)`}
          field="regulagem"
          body={regulagemTemplate}
          hidden={sementeCobertura}
        />
        <Column
          {...defaultColumnProps()}
          header="PMS (gr)"
          body={(talhao) =>
            formatNumber(talhao.pms || 0, NUMBER_FORMAT_OPTIONS)
          }
          field="pms"
          hidden={sementeCobertura || isCulturaPorUnidade}
        />
        <Column
          {...defaultColumnProps()}
          header="Qtd"
          field="quantidade.resultado"
          body={quantidadeTemplate}
        />
        <Column
          {...defaultColumnProps()}
          header="Total"
          field="quantidadeTotal.total"
          body={(talhao) =>
            getTotalLabel(talhao.quantidadeTotal.total, talhao.unidade)
          }
        />
        <Column
          {...defaultColumnProps()}
          header="Ciclo"
          field="ciclo"
          body={(talhao) => defaultNumberFormatter(talhao.ciclo)}
        />
        <Column
          {...defaultColumnProps()}
          header="Colheita"
          field="dataColheita"
          body={(talhao) => humanizeDate(talhao.dataColheita)}
        />
      </AutoSortTable>
    </div>
  );
};

type PropTypes = {
  formValues: {
    talhoes: TalhaoRow[];
    sementeCobertura: boolean;
    comMargemSeguranca: boolean;
    distribuicaoRegulagemSemente?: number;
    porcentagemCorrecaoDensidade?: number;
    porcentagemAreaRemonte?: number;
    porcentagemCorrecaoQuantidade?: number;
    espacamento?: number;
    rendimentoOperacionalMaximo?: number;
    rendimentoOperacionalMinimo?: number;
    atividade?: Atividade;
  };
  striped?: boolean;
  printable?: boolean;
};

export default SementesList;
